import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  getThemePalette,
  normalizeThemePaletteId,
  resolveThemeTokens,
  type ThemeMode,
  type ThemePalette,
  type ThemePaletteId,
} from "./palettes";
import {
  readStoredHighContrast,
  readStoredThemeMode,
  readStoredThemePaletteId,
  readStoredThemePaletteSelection,
  readStoredUnlockedThemeIds,
  THEME_CHANGED_EVENT,
  writeThemeBootstrapSnapshot,
  writeStoredHighContrast,
  writeStoredThemeMode,
  writeStoredThemePaletteId,
} from "./theme-storage";

const ThemeContext = createContext<{
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  resolvedMode: "light" | "dark";
  paletteId: ThemePaletteId;
  palette: ThemePalette;
  setPaletteId: (id: ThemePaletteId) => void;
  syncedPaletteId: string;
  paletteSupported: boolean;
  highContrast: boolean;
  systemHighContrast: boolean;
  setHighContrast: (enabled: boolean) => void;
  unlockedPaletteIds: readonly string[];
} | null>(null);

function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode === "light" || mode === "dark") return mode;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function prefersHighContrast(): boolean {
  return window.matchMedia("(prefers-contrast: more)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredThemeMode());
  const [paletteId, setPaletteIdState] = useState<ThemePaletteId>(() => readStoredThemePaletteId());
  const [syncedPaletteId, setSyncedPaletteId] = useState(
    () => readStoredThemePaletteSelection().storedId,
  );
  const [paletteSupported, setPaletteSupported] = useState(
    () => readStoredThemePaletteSelection().supported,
  );
  const [manualHighContrast, setManualHighContrast] = useState(() => readStoredHighContrast());
  const [systemHighContrast, setSystemHighContrast] = useState(() => prefersHighContrast());
  const [unlockedPaletteIds, setUnlockedPaletteIds] = useState<readonly string[]>(() =>
    readStoredUnlockedThemeIds(),
  );
  const [resolvedMode, setResolvedMode] = useState<"light" | "dark">(() => resolveTheme(mode));
  const highContrast = manualHighContrast || systemHighContrast;

  const apply = useCallback(
    (
      m: ThemeMode,
      id: ThemePaletteId,
      contrast: boolean,
      manualContrast: boolean,
      sourceId: string,
    ) => {
      const resolved = resolveTheme(m);
      const tokens = resolveThemeTokens(id, resolved, contrast);
      const root = document.documentElement;
      for (const [name, value] of Object.entries(tokens.cssVars)) {
        root.style.setProperty(name, value);
      }
      root.style.colorScheme = resolved;
      root.setAttribute("data-theme", resolved);
      root.setAttribute("data-theme-mode", m);
      root.setAttribute("data-theme-palette", id);
      root.setAttribute("data-high-contrast", String(contrast));
      if (sourceId === id) {
        root.removeAttribute("data-synced-theme-palette");
      } else {
        root.setAttribute("data-synced-theme-palette", sourceId);
      }
      setResolvedMode(resolved);
      document.getElementById("tb-theme-color")?.setAttribute("content", tokens.metaThemeColor);
      writeThemeBootstrapSnapshot({
        mode: m,
        resolved,
        paletteId: id,
        highContrast: contrast,
        manualHighContrast: manualContrast,
        tokens,
      });
    },
    [],
  );

  useEffect(() => {
    apply(mode, paletteId, highContrast, manualHighContrast, syncedPaletteId);
    const colorQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const contrastQuery = window.matchMedia("(prefers-contrast: more)");
    const onColorChange = () => {
      if (mode === "system") {
        apply("system", paletteId, highContrast, manualHighContrast, syncedPaletteId);
      }
    };
    const onContrastChange = (event: MediaQueryListEvent) => {
      setSystemHighContrast(event.matches);
    };
    colorQuery.addEventListener("change", onColorChange);
    contrastQuery.addEventListener("change", onContrastChange);
    return () => {
      colorQuery.removeEventListener("change", onColorChange);
      contrastQuery.removeEventListener("change", onContrastChange);
    };
  }, [mode, paletteId, syncedPaletteId, highContrast, manualHighContrast, apply]);

  useEffect(() => {
    const refreshStoredTheme = () => {
      const selection = readStoredThemePaletteSelection();
      const nextMode = readStoredThemeMode();
      const nextManualHighContrast = readStoredHighContrast();
      const nextSystemHighContrast = prefersHighContrast();
      setModeState(nextMode);
      setPaletteIdState(selection.renderId);
      setSyncedPaletteId(selection.storedId);
      setPaletteSupported(selection.supported);
      setManualHighContrast(nextManualHighContrast);
      setSystemHighContrast(nextSystemHighContrast);
      setUnlockedPaletteIds(readStoredUnlockedThemeIds());
      // Apply immediately even when two account scopes happen to hold identical
      // React state, so the newly active scope receives its own bootstrap snapshot.
      apply(
        nextMode,
        selection.renderId,
        nextManualHighContrast || nextSystemHighContrast,
        nextManualHighContrast,
        selection.storedId,
      );
    };
    let storageRefreshTimer: number | undefined;
    const refreshAfterStorageReconciliation = (event: StorageEvent) => {
      if (
        event.key !== null &&
        event.key !== "tb_auth_session_v1" &&
        !event.key.startsWith("tb_web_theme_")
      ) {
        return;
      }
      if (storageRefreshTimer !== undefined) window.clearTimeout(storageRefreshTimer);
      // AuthProvider reconciles its in-memory session during the same storage event.
      // Read the account-scoped theme after every listener has observed that transition.
      storageRefreshTimer = window.setTimeout(refreshStoredTheme, 0);
    };
    window.addEventListener(THEME_CHANGED_EVENT, refreshStoredTheme);
    window.addEventListener("tb-auth-changed", refreshStoredTheme);
    window.addEventListener("storage", refreshAfterStorageReconciliation);
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, refreshStoredTheme);
      window.removeEventListener("tb-auth-changed", refreshStoredTheme);
      window.removeEventListener("storage", refreshAfterStorageReconciliation);
      if (storageRefreshTimer !== undefined) window.clearTimeout(storageRefreshTimer);
    };
  }, [apply]);

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      writeStoredThemeMode(m);
    } catch {
      /* ignore */
    }
  }, []);

  const setPaletteId = useCallback((id: ThemePaletteId) => {
    const next = normalizeThemePaletteId(id);
    setPaletteIdState(next);
    setSyncedPaletteId(next);
    setPaletteSupported(true);
    try {
      writeStoredThemePaletteId(next);
    } catch {
      /* ignore */
    }
  }, []);

  const setHighContrast = useCallback((enabled: boolean) => {
    setManualHighContrast(enabled);
    try {
      writeStoredHighContrast(enabled);
    } catch {
      /* ignore */
    }
  }, []);

  const palette = useMemo(() => getThemePalette(paletteId), [paletteId]);

  const value = useMemo(
    () => ({
      mode,
      setMode,
      resolvedMode,
      paletteId,
      palette,
      setPaletteId,
      syncedPaletteId,
      paletteSupported,
      highContrast,
      systemHighContrast,
      setHighContrast,
      unlockedPaletteIds,
    }),
    [
      mode,
      setMode,
      resolvedMode,
      paletteId,
      palette,
      setPaletteId,
      syncedPaletteId,
      paletteSupported,
      highContrast,
      systemHighContrast,
      setHighContrast,
      unlockedPaletteIds,
    ],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme outside ThemeProvider");
  return ctx;
}
